import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pencil, Check, Loader2, Sparkles, Eye, MapPin } from 'lucide-react';
import { ProfilePictureUploadGeneric } from '@/components/profile/profile-picture-upload-generic';
import { SlideIn } from '@/components/motion';
import { cn } from '@/lib/utils';
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog';

interface ProfileHeaderProps {
  freelancerProfileId: string | undefined;
  profilePictureUrl: string | null;
  displayName: string;
  headline: string;
  location?: string;
  reviewStatus?: string | null | undefined;
  viewCount?: number;
  isEditingNameHeadline: boolean;
  tempDisplayName: string;
  tempHeadline: string;
  tempLocation: string;
  isSavingNameHeadline: boolean;
  isAIGenerating: boolean;
  generatingType: string | null;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDisplayNameChange: (value: string) => void;
  onHeadlineChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  isAIHeadlineDialogOpen: boolean;
  aiGeneratedHeadline: string | null;
  onAIHeadlineDialogOpenChange: (open: boolean) => void;
  onGenerateHeadline: (currentText: string | undefined, options: any) => Promise<string | null>;
  onAcceptHeadline: (text: string) => void;
  onResetAIData: () => void;
}

export function ProfileHeader({
  freelancerProfileId,
  profilePictureUrl,
  displayName,
  headline,
  location,
  reviewStatus,
  viewCount,
  isEditingNameHeadline,
  tempDisplayName,
  tempHeadline,
  tempLocation,
  isSavingNameHeadline,
  isAIGenerating,
  generatingType,
  onEditClick,
  onCancel,
  onSave,
  onDisplayNameChange,
  onHeadlineChange,
  onLocationChange,
  isAIHeadlineDialogOpen,
  aiGeneratedHeadline,
  onAIHeadlineDialogOpenChange,
  onGenerateHeadline,
  onAcceptHeadline,
  onResetAIData,
}: ProfileHeaderProps) {
  return (
    <SlideIn direction="left">
      <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 lg:p-8">
        <div className="flex flex-col items-center gap-6">
          {/* Avatar with edit button */}
          <div className="relative">
            <ProfilePictureUploadGeneric
              profileType="freelancer"
              profileId={freelancerProfileId}
              currentUrl={profilePictureUrl}
              name={displayName || ''}
            />
          </div>

          {/* Name and Headline */}
          <div className="text-center space-y-3 w-full relative">
            {isEditingNameHeadline ? (
              <div className="space-y-4 w-full">
                <div className="space-y-1 text-left">
                  <Label htmlFor="displayName" className="text-sm">Name</Label>
                  <Input
                    id="displayName"
                    value={tempDisplayName}
                    onChange={(e) => onDisplayNameChange(e.target.value)}
                    maxLength={50}
                    className="text-center text-[32px] font-normal tracking-tight h-auto rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
                    placeholder="Your Name"
                  />
                </div>
                <div className="space-y-1 text-left">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="headline" className="text-sm">Headline</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onAIHeadlineDialogOpenChange(true)}
                      disabled={isAIGenerating}
                      className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
                    >
                      {isAIGenerating && generatingType === 'headline' ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-3 w-3 mr-1" />
                          Generate headline with AI
                        </>
                      )}
                    </Button>
                  </div>
                  <AIAssistantDialog
                    open={isAIHeadlineDialogOpen}
                    onOpenChange={(open) => {
                      onAIHeadlineDialogOpenChange(open);
                      if (!open) {
                        onResetAIData();
                      }
                    }}
                    onGenerate={onGenerateHeadline}
                    onAccept={onAcceptHeadline}
                    generatedText={aiGeneratedHeadline}
                    isLoading={isAIGenerating && generatingType === 'headline'}
                    type="headline"
                    currentText={tempHeadline}
                  />
                  <Input
                    id="headline"
                    value={tempHeadline}
                    onChange={(e) => onHeadlineChange(e.target.value)}
                    maxLength={100}
                    className="text-center text-xl text-slate-500 h-auto rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
                    placeholder="AI/ML Specialist"
                  />
                </div>
                <div className="space-y-1 text-left">
                  <Label htmlFor="location" className="text-sm">Location</Label>
                  <Input
                    id="location"
                    value={tempLocation}
                    onChange={(e) => onLocationChange(e.target.value)}
                    className="text-center text-sm text-slate-500 h-auto rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onCancel}
                    disabled={isSavingNameHeadline}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={onSave}
                    disabled={isSavingNameHeadline}
                    className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingNameHeadline ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onEditClick}
                  className="absolute top-[-10px] right-[-12px] text-[#4669ed] hover:text-[#2b42a3] p-1 h-6 w-6"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <div className="inline-block max-w-full">
                  <h1 className="text-2xl sm:text-[28px] lg:text-[32px] font-normal tracking-tight text-slate-900 break-words overflow-hidden">
                    {displayName || 'Your Name'}
                  </h1>
                </div>
                <p className="text-xl text-slate-500">
                  {headline || 'Your Headline'}
                </p>

                {/* Location and View Count */}
                {(location || viewCount !== undefined) && (
                  <div className="flex items-center justify-center gap-3 text-sm text-slate-500 pt-2">
                    {location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>{location}</span>
                      </div>
                    )}
                    {location && viewCount !== undefined && (
                      <span className="text-slate-300">|</span>
                    )}
                    {viewCount !== undefined && (
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        <span>
                          {viewCount === 1
                            ? '1 view'
                            : `${viewCount || 0} views`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Review Status Badge */}
            {reviewStatus && (
              <div className="flex justify-center pt-2">
                <Badge className={cn(
                  "rounded-full px-5 py-2 text-xs font-semibold tracking-wide uppercase shadow-sm border",
                  reviewStatus === 'approved' 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  reviewStatus === 'pending' 
                    ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  reviewStatus === 'changes_requested' 
                    ? 'bg-orange-50 text-orange-700 border-orange-200' :
                    'bg-slate-50 text-slate-700 border-slate-200'
                )}>
                  {reviewStatus === 'approved' ? '✓ Approved' :
                   reviewStatus === 'pending' ? '⏳ Pending Review' :
                   reviewStatus === 'changes_requested' ? '✎ Changes Requested' :
                   'Draft'}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </div>
    </SlideIn>
  );
}
